/**
 * WebSocket hook for real-time notifications.
 * Connects to the Django Channels WebSocket and handles incoming notifications.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { API_BASE_URL } from '../constants/config';

interface Notification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  is_read: boolean;
  link?: string;
}

interface UseNotificationWebSocketResult {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
}

export const useNotificationWebSocket = (
  token: string | null,
  isAuthenticated: boolean
): UseNotificationWebSocketResult => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const getWebSocketUrl = useCallback(() => {
    if (!token) return null;

    // Convert HTTP URL to WebSocket URL
    let wsUrl = API_BASE_URL.replace(/^http/, 'ws');

    // Remove /api/v1 suffix if present
    wsUrl = wsUrl.replace(/\/api\/v1\/?$/, '');

    return `${wsUrl}/ws/notifications/?token=${token}`;
  }, [token]);

  const connect = useCallback(() => {
    // Skip WebSocket if no token or not authenticated
    if (!token) {
      console.log('⚠️ WebSocket: No token, skipping connection');
      return;
    }

    if (!isAuthenticated) {
      console.log('⚠️ WebSocket: Not authenticated, skipping connection');
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.log('⚠️ WebSocket: Could not generate URL, skipping');
      return;
    }

    // Close existing connection if any
    if (socketRef.current) {
      socketRef.current.close();
    }

    try {
      console.log('🔌 WebSocket: Attempting connection...');
      // WebSocket connection - errors are handled silently since WS server may not be running
      const socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('✅ WebSocket: Connected successfully');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'connection_established':
              setUnreadCount(data.unread_count || 0);
              break;

            case 'new_notification':
              // Add new notification to the top of the list
              setNotifications((prev) => [data.notification, ...prev]);
              setUnreadCount((prev) => prev + 1);

              // Show browser notification if supported (web only)
              if (Platform.OS === 'web') {
                showBrowserNotification(data.notification);
              }
              break;

            case 'unread_count':
              setUnreadCount(data.count);
              break;

            case 'error':
              console.error('WebSocket server error:', data.message);
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socket.onclose = (event) => {
        setIsConnected(false);

        // Attempt to reconnect if not a normal close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          console.log(`⚠️ WebSocket: Connection closed (code: ${event.code}), reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (event.code === 1000) {
          console.log('🔌 WebSocket: Connection closed normally');
        } else {
          console.log(`⚠️ WebSocket: Max reconnect attempts (${maxReconnectAttempts}) reached, giving up`);
        }
      };

      socket.onerror = () => {
        // Silently handle WebSocket errors - connection will be retried automatically
        // The WS server may not be running, which is OK
        console.log('⚠️ WebSocket: Connection error (server may not be running)');
      };

      socketRef.current = socket;
    } catch (e) {
      // WebSocket server may not be running - fail silently
      console.log('⚠️ WebSocket: Failed to create connection', e);
    }
  }, [token, isAuthenticated, getWebSocketUrl]);

  // Connect when token/auth changes
  useEffect(() => {
    if (token && isAuthenticated) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close(1000);
      }
    };
  }, [token, isAuthenticated, connect]);

  const markAsRead = useCallback((notificationId: number) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          action: 'mark_read',
          notification_id: notificationId,
        })
      );

      // Optimistically update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          action: 'mark_all_read',
        })
      );

      // Optimistically update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    setNotifications,
    setUnreadCount,
  };
};

/**
 * Show browser notification (web only)
 */
export const showBrowserNotification = (notification: { id: number; title: string; message: string }) => {
  if (Platform.OS !== 'web') return;

  // Check if browser supports notifications
  if (!('Notification' in window)) return;

  // Request permission if not granted
  if (Notification.permission === 'default') {
    Notification.requestPermission();
    return;
  }

  // Show notification if permitted
  if (Notification.permission === 'granted') {
    new Notification(notification.title, {
      body: notification.message,
      icon: '/logo.png',
      tag: `notification-${notification.id}`,
    });
  }
};

export default useNotificationWebSocket;
